import * as React from 'react'
import { cn } from '@/lib/utils'
import { GlassCard } from './glass-card'
import { AnimatedNumber } from './animated-number'
import type { LucideIcon } from 'lucide-react'

type Tone = 'orange' | 'green' | 'blue' | 'red' | 'amber' | 'violet' | 'cyan' | 'default'

const TONE_ICON: Record<Tone, string> = {
  default: 'text-neutral-600 bg-neutral-100 dark:text-neutral-700 dark:text-white/70 dark:bg-neutral-50 dark:bg-white/5',
  orange: 'text-orange-600 bg-orange-50 dark:text-orange-300 dark:bg-orange-500/10',
  green: 'text-emerald-600 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-500/10',
  blue: 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-500/10',
  red: 'text-rose-600 bg-rose-50 dark:text-rose-300 dark:bg-rose-500/10',
  amber: 'text-amber-600 bg-amber-50 dark:text-amber-300 dark:bg-amber-500/10',
  violet: 'text-violet-600 bg-violet-50 dark:text-violet-300 dark:bg-violet-500/10',
  cyan: 'text-cyan-600 bg-cyan-50 dark:text-cyan-300 dark:bg-cyan-500/10',
}

interface KpiTileProps {
  label: string
  value: number | string
  icon?: LucideIcon
  tone?: Tone
  hint?: string
  trend?: { direction: 'up' | 'down' | 'flat'; label: string }
  format?: (n: number) => string
  className?: string
  onClick?: React.MouseEventHandler<HTMLDivElement>
}

export function KpiTile({ label, value, icon: Icon, tone = 'default', hint, trend, format, className, onClick }: KpiTileProps) {
  const isNumeric = typeof value === 'number'
  return (
    <GlassCard tone={tone} interactive={!!onClick} onClick={onClick} className={cn('min-h-[110px]', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-500 dark:text-white/50">{label}</p>
          <p className="mt-2 text-2xl md:text-3xl font-semibold text-neutral-900 dark:text-white tabular-nums tracking-tight">
            {isNumeric
              ? <AnimatedNumber value={value as number} format={format} />
              : value}
          </p>
          {hint && <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-400 dark:text-white/40 truncate">{hint}</p>}
        </div>
        {Icon && (
          <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', TONE_ICON[tone])}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
      {trend && (
        <p className={cn(
          'mt-2 inline-flex items-center gap-1 text-[11px] font-medium',
          trend.direction === 'up' && 'text-emerald-600 dark:text-emerald-400',
          trend.direction === 'down' && 'text-rose-600 dark:text-rose-400',
          trend.direction === 'flat' && 'text-neutral-500 dark:text-neutral-500 dark:text-white/50',
        )}>
          {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '·'} {trend.label}
        </p>
      )}
    </GlassCard>
  )
}
